import React, { useState, useEffect, useContext, KeyboardEvent, ChangeEvent } from "react";
import Message from "./Components/Message/Message";
import { useNavigate } from "react-router-dom";
import useEscapeRoomApi from "./Hooks/useEscapeRoomApi";
import { GameContext } from "./Context/GameContext";
import RoomsIcons from "./Components/RoomsIcons/RoomsIcons";
import Header from "./Components/Header/Header";
import { introCompleted, introDefault, introSinglePlayRooms } from "./Config/config";
import { RoomStatus } from "./Types/Game";
import ActicaGroup from "./Components/Branding/GroupLogos";
import ControlButtons from "./Components/ControlButtons/ControlButtons";
import "./Start.css";

/**
 * Start
 * -----
 * Landing / entry screen for teams before they begin an escape-room run.
 *
 * Behaviour:
 * - Accepts team & (optionally) school names, validates via API, and advances game state.
 * - Auto-selects the single available room when only one is open.
 * - Shows guidance text based on profile settings and current progress.
 */

/** CONSTANTS ---------------------------------------- */
// (none currently) — keep this section for future static config

const Start: React.FC = () => {
  /** CONTEXT ----------------------------------------- */
  // App-wide state and helpers provided by GameContext
  const {
    eventName,
    setTeamName,
    progressGame,
    roomStates,
    profileSettings,
    hasLoadedRoomStates,
    setCurrentRoom,
    currentRoom,
    teamName,
    setSchoolName,
    schoolName,
  } = useContext(GameContext);

  // Safe local aliases (roomStates/profileSettings may be undefined until loaded)
  const rooms = roomStates ?? {};

  /** STATE ------------------------------------------- */
  // Local, controlled inputs for team and school names (start as empty strings)
  const [localTeamName, setLocalTeamName] = useState("");
  const [localSchoolName, setLocalSchoolName] = useState("");

  // Simple validation / messaging flags
  const [containsBannedWord, setContainsBannedWord] = useState(false);
  const [bannedWordCount, setBannedWordCount] = useState(0);
  const [activeRoomCount, setActiveRoomCount] = useState(0);
  const [message, setMessage] = useState("");

  // Navigation + API helpers
  const navigate = useNavigate();
  const { checkNameExists } = useEscapeRoomApi();

  /** EFFECTS ----------------------------------------- */
  // When roomStates are available, auto-select the single available open room (if only one)
  useEffect(() => {
    if (!hasLoadedRoomStates) return;

    const openActiveIds = Object.entries(roomStates ?? {})
      .filter(([, r]) => r.active && r.status === RoomStatus.Open)
      .map(([id]) => id);

    if (openActiveIds.length === 1) {
      const onlyId = openActiveIds[0];
      if (currentRoom !== onlyId) {
        setCurrentRoom(onlyId); // auto-select the sole room
      }
    }
  }, [hasLoadedRoomStates, roomStates, currentRoom, setCurrentRoom]);

  // Compute whether all active rooms are completed/trapped (used to change intro copy)
  const allCompleted =
    hasLoadedRoomStates &&
    Object.values(rooms)
      .filter((room) => room.active)
      .every((room) => room.status === RoomStatus.Completed || room.status === RoomStatus.Trapped);

  // Update activeRoomCount whenever roomStates or profileSettings change
  useEffect(() => {
    if (!hasLoadedRoomStates) return;

    const countActiveRooms = () => {
      const activeRooms = Object.values(roomStates ?? {}).filter(
        (room) => room.active && room.status === RoomStatus.Open
      ).length;
      setActiveRoomCount(profileSettings?.singleRoom ? 1 : activeRooms);
    };

    countActiveRooms();
  }, [hasLoadedRoomStates, roomStates, profileSettings]);

  /** HANDLERS ---------------------------------------- */
  // Controlled input change handlers — store locally while editing
  const handleTeamNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalTeamName(event.target.value);
  };

  const handleSchoolNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalSchoolName(event.target.value);
  };

  // Keyboard convenience: Enter triggers start action
  const handleKeyDownEvent = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleStartClick();
    }
  };

  // Primary start button handler — validates and either progresses or requests a name-check
  const handleStartClick = async () => {
    // If the profile is single-room and the user hasn't chosen a room, prompt them
    if (profileSettings?.singleRoom && !currentRoom) {
      setMessage("Select an Escape Room that you would like to play!");
      return;
    }

    // Reset transient messages/state for a clean validation run
    setMessage("");
    setContainsBannedWord(false);

    // Basic client-side validation
    if (!localTeamName.trim() && !teamName) {
      setMessage("Please enter a team name.");
      return;
    }

    if (profileSettings?.schoolInput && !schoolName && !localSchoolName?.trim()) {
      setMessage("Please enter a school name.");
      return;
    }

    try {
      // If a team name already exists in context, just progress (we assume validated earlier)
      if (teamName) {
        progressGame();
        navigate("/escaperoom");
        return;
      }

      // Validate team name via server / local storage API
      const response = await checkNameExists(localTeamName, localSchoolName, eventName ?? "");

      if (response?.exists === true) {
        setMessage("Team name already exists. Please choose another name.");
      } else if (response?.banned) {
        // Track repeated banned attempts to show escalating messaging
        setContainsBannedWord(true);
        setBannedWordCount((c) => c + 1);
      } else if (response?.message) {
        setMessage(response.message);
      } else if (response?.exists === false) {
        // Success: apply team/school to global state and start the game
        setTeamName(localTeamName);
        setSchoolName(localSchoolName);
        progressGame();
        navigate("/escaperoom");
      } else {
        setMessage("An unexpected error occurred. Please try again.");
      }
    } catch (error) {
      // Surface unexpected network / API errors to the user
      console.error("Error checking team name:", error);
      setMessage("An error occurred while checking the team name.");
    }
  };

  // Count actual open+active rooms (ignoring single-room mode)
  const openRoomCount =
    hasLoadedRoomStates
      ? Object.values(rooms).filter(
          (r) => r && r.active && r.status === RoomStatus.Open
        ).length
      : 0;

  // In this app, selectable mode === singleRoom
  const canSelectRoom = Boolean(profileSettings?.singleRoom);
  const showSelectPrompt = canSelectRoom && openRoomCount > 1;

  /** RENDER -------------------------------------------- */
  // Show nothing until room states are loaded to avoid layout flicker
  if (!hasLoadedRoomStates) return null;

  return (
    <div className="escape-room lobby-screen">
      <ControlButtons />
      <div className="flex-container">
        <div className="box box-round box-white box-start">
          <Header
            eventName={eventName}
            content={
              allCompleted
                ? introCompleted
                : profileSettings?.singleRoom
                  ? introSinglePlayRooms
                  : introDefault(activeRoomCount)
            }
          />

          <div className="start-form">
            {/* Team name: controlled input that prefers context `teamName` when present */}
            <label className="label-text" style={{ padding: "10px 16px 8px 16px"}} htmlFor="teamName">Team Name:</label>
            <input
              className="start-input"
              name="teamName"
              id="teamName"
              type="text"
              value={teamName ?? localTeamName}
              onChange={handleTeamNameChange}
              onKeyDown={handleKeyDownEvent}
              disabled={!!teamName}
              placeholder="Enter a name for your team ..."
              aria-label="Team name"
            />

            {/* Optional school name */}
            {profileSettings?.schoolInput ? (
              <div>
                <label className="label-text" style={{ padding: "10px 16px 8px 16px"}} htmlFor="schoolName">School Name:</label>
                <input
                  className="start-input"
                  name="schoolName"
                  id="schoolName"
                  type="text"
                  value={schoolName ?? localSchoolName}
                  disabled={!!schoolName}
                  onChange={handleSchoolNameChange}
                  onKeyDown={handleKeyDownEvent}
                  placeholder="Enter your school name ..."
                  aria-label="School name"
                />
              </div>
            ) : null}

            {/* Validation / feedback messages */}
            {containsBannedWord && (
              <Message message="Your team name cannot contain obscene language." type="error" />
            )}
            {bannedWordCount === 3 && (
              <Message message="Please choose a friendly team name that everyone can enjoy!" type="error" />
            )}
            {message && <Message message={message} type="error" />}
          </div>

          {showSelectPrompt && (
            <p style={{ fontWeight: "bold", width: "100%", textAlign: "center", fontSize: "17px" }}>
              Select an Escape Room ...
            </p>
          )}

          <RoomsIcons />

          {!allCompleted && (
            <div className="button-rail">
              <button className="btn-blue" onClick={handleStartClick}>
                {profileSettings?.singleRoom ? "Play room" : "Start the challenge!"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Branding / credits (decorative) */}
      <div className="lobby-credits">
        <ActicaGroup />
      </div>

    </div>
  );
};

export default Start;
